import classNames from 'classnames';
import * as _ from 'lodash';

import { setupForDark } from '@theme';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InputErrorMessage from './InputErrorMessage';

const secondsToPace = (v: number) => {
  const minutes = Math.floor((v % 3600) / 60);
  const seconds = v % 60;
  return {
    minutes,
    seconds
  };
};

const paceToSeconds = (t: { minutes: number; seconds: number }) => {
  return t.minutes * 60 + t.seconds;
};

const formatPace = (pace: { minutes: number; seconds: number }) => {
  const seconds = pace.seconds < 10 ? `0${pace.seconds}` : pace.seconds;

  return `${pace.minutes}:${seconds}`;
};

const formatSeconds = (seconds: number) => {
  return formatPace(secondsToPace(seconds));
};

function PaceInput({
  displayMode = 'default',
  value,
  minValue,
  maxValue,
  onChange,
  onError
}: {
  displayMode?: 'default';
  value: number;
  minValue: number;
  maxValue: number;
  onChange: (value: number) => void;
  onError?: (error: string) => void;
}) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState({
    minutes: 0,
    seconds: 0
  });
  const [error, setError] = useState<string | null>(null);
  const onErrorMessage = (error: string) => {
    if (onError) {
      onError(error);
    }
    setError(error);
  };

  const validate = (pace: { minutes: number; seconds: number }): boolean => {
    // MINUTES
    if (_.isNaN(pace.minutes)) {
      onErrorMessage(t('components.paceInput.errorWrongMinutesValue', { min: 0, max: 59 }));
      return false;
    }

    // SECONDS
    if (_.isNaN(pace.seconds)) {
      onErrorMessage(t('components.paceInput.errorWrongSecondsValue', { min: 0, max: 59 }));
      return false;
    }

    const paceInSeconds = paceToSeconds(pace);
    if (paceInSeconds < minValue || paceInSeconds > maxValue) {
      onErrorMessage(
        t('components.paceInput.errorWrongValueNoMinMax', {
          min: formatSeconds(minValue),
          max: formatSeconds(maxValue)
        })
      );
      return false;
    }

    if (!pace || (pace.minutes === 0 && pace.seconds === 0)) {
      onErrorMessage(t('components.paceInput.errorWrongValue'));
      return false;
    }

    onErrorMessage(null);
    return true;
  };

  useEffect(() => {
    setInputValue(secondsToPace(value));
  }, [value]);

  // check for errors only if the minValue, maxValue or inputValue changes
  useEffect(() => {
    validate(inputValue);
  }, [minValue, maxValue, inputValue]);

  // this component should not be null or undefined
  if (_.isNil(value)) {
    return <></>;
  }

  const onMinutesChange = (event) => {
    let minutes = parseInt(event.target.value);
    if (!_.isNaN(minutes)) {
      minutes = Math.min(minutes, 59);
    }

    setInputValue((prev) => ({
      ...prev,
      minutes: minutes
    }));

    if (
      validate({
        minutes: minutes,
        seconds: inputValue.seconds
      })
    ) {
      onChange(
        paceToSeconds({
          minutes: minutes,
          seconds: inputValue.seconds
        })
      );
    }
  };

  const onSecondsChange = (event) => {
    let seconds = parseInt(event.target.value);
    if (!_.isNaN(seconds)) {
      seconds = Math.min(seconds, 59);
    }

    setInputValue((prev) => ({
      ...prev,
      seconds: seconds
    }));

    if (
      validate({
        minutes: inputValue.minutes,
        seconds: seconds
      })
    ) {
      onChange(
        paceToSeconds({
          minutes: inputValue.minutes,
          seconds: seconds
        })
      );
    }
  };

  const onKeyDown = (e) => {
    let keyCode = e.keyCode || e.which;
    let keyValue = String.fromCharCode(keyCode);

    if (
      e.key === 'Backspace' ||
      keyCode === 8 ||
      e.key === 'Delete' ||
      keyCode === 46 ||
      e.key === 'Tab' ||
      keyCode === 9 ||
      e.key === 'ArrowLeft' ||
      keyCode === 37 ||
      e.key === 'ArrowUp' ||
      keyCode === 38 ||
      e.key === 'ArrowRight' ||
      keyCode === 39 ||
      e.key === 'ArrowDown' ||
      keyCode === 40
    ) {
      return;
    }

    if (!/\d/.test(keyValue)) {
      e.preventDefault();
    }
  };

  const onFocus = (e) => {
    e.target.select();
    setTimeout(() => {
      e.target.scrollIntoView();
    }, 500);
  };

  return (
    <div>
      <div
        className={classNames(
          'rounded-xs px-sm py-xs',
          setupForDark('bg-text-input-default'),
          setupForDark('text-primary'),
          setupForDark('border-text-input-default'),
          'border-[1px]',
          'P2M w-fit',
          'flex flex-row items-center justify-start',
          error && onError ? setupForDark('border-error') : '',
          error && onError ? 'border-[1px]' : ''
        )}
      >
        <input
          name='minutes'
          type='number'
          step='1'
          value={_.isNil(inputValue.minutes) ? '' : inputValue.minutes}
          onChange={onMinutesChange}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          className={classNames(
            'bg-transparent border-none outline-none hide-input-arrows text-right pr-[2px] w-[26px]'
          )}
        />
        <div>:</div>
        <input
          name='seconds'
          type='number'
          step='1'
          value={_.isNil(inputValue.seconds) ? '' : inputValue.seconds}
          onChange={onSecondsChange}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          className={classNames(
            'bg-transparent border-none outline-none hide-input-arrows text-left pr-[2px] w-[26px]'
          )}
        />
      </div>
      {error && !onError && <InputErrorMessage error={error} />}
    </div>
  );
}

export default PaceInput;
